import { Component, HostListener, OnInit, signal } from "@angular/core";
import { MenuService } from "../../services/menu.service";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../services/auth/auth.service";
import { User } from "../../../admin/models/users.model";
import { ThemeService } from "../../services/theme.service";
import { ThemeToggleComponent } from "../../components/theme-toggle/theme-toggle.component";

@Component({
  selector: "app-navbar",
  standalone: true,
  imports: [NgClass, NgIf, ThemeToggleComponent, NgForOf],
  templateUrl: "./navbar.component.html",
  styleUrl: "./navbar.component.css",
  animations: [
    trigger("openClose", [
      state(
        "open",
        style({
          opacity: 1,
          transform: "translateY(0)",
          visibility: "visible",
        })
      ),
      state(
        "closed",
        style({
          opacity: 0,
          transform: "translateY(-20px)",
          visibility: "hidden",
        })
      ),
      transition("open => closed", [animate("0.2s")]),
      transition("closed => open", [animate("0.2s")]),
    ]),
  ],
})
export class NavbarComponent implements OnInit {
  userAuth = signal<any>([]);
  user = signal<User | null>(null);
  statNotif = signal<boolean>(false);
  constructor(
    private menuService: MenuService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public themeService: ThemeService
  ) { }

  ngOnInit(): void {
    const jwtToken = localStorage.getItem("jwtToken");
    if (jwtToken) {
      this.user.set(this.route.snapshot.data["user"].data);
      if (this.user()) {
        this.userAuth.set(this.user());
      }
    }
  }

  getCurrentPageName(): string {
    const url = this.router.url;
    const segments = url.split('/').filter(s => s && s !== 'admin' && s !== 'company');

    // Si le dernier segment est un ID (nombre ou UUID), prendre le segment précédent
    let lastSegment = segments[segments.length - 1] || 'Tableau de Bord';

    // Vérifier si c'est un ID (nombre ou pattern UUID/GUID)
    const isId = /^[0-9]+$/.test(lastSegment) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSegment);

    if (isId && segments.length > 1) {
      // Prendre le segment précédent (ex: "companies" au lieu de "123")
      lastSegment = segments[segments.length - 2];

      // Si c'est "edit" ou "add", prendre encore le segment précédent
      if ((lastSegment === 'edit' || lastSegment === 'add') && segments.length > 2) {
        lastSegment = segments[segments.length - 3];
      }
    }

    // Nettoyer et formater
    lastSegment = lastSegment
      .replace(/-/g, ' ')  // Remplacer les tirets par des espaces
      .replace(/_/g, ' '); // Remplacer les underscores par des espaces

    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
  }

  public toggleNotification() {
    this.statNotif.set(!this.statNotif());
  }

  public toggleMobileMenu(): void {
    this.menuService.showMobileMenu = !this.menuService.showMobileMenu;
  }
  public isOpen = false;
  public profileMenu: any = [
    {
      title: "Profil",
      icon: "M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
      link: "/profile/me",
      active: true,
      action: () => {
        this.isOpen = !this.isOpen;
        this.userAuth().plateform === "BACKOFFICE_ADMIN"
          ? this.router
            .navigate(["/admin/profile/me"])
            .then((r) => console.log("Admin Profile..."))
          : this.router
            .navigate(["/company/profile/me"])
            .then((r) => console.log("Company Profile..."));
      },
      description: "Pas encore disponible",
    },
    {
      title: "Log out",
      icon: "M5.636 5.636a9 9 0 1 0 12.728 0M12 3v9",
      link: "/auth",
      active: true,
      action: () => {
        this.logout();
      },
      description: "Se déconnecter",
    },
  ];

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdown = document.querySelector(".absolute");
    if (dropdown && !dropdown.contains(target)) {
      this.isOpen = false;
    }
  }

  onDropdownClick(event: MouseEvent): void {
    event.stopPropagation(); // Empêche le clic à l'intérieur de fermer le dropdown
  }
  public toggleMenu(event: MouseEvent): void {
    event.stopPropagation(); // Empêche le clic à l'intérieur de fermer le dropdown
    this.isOpen = !this.isOpen;
  }

  public toggleSidebar() {
    this.menuService.toggleSidebar();
  }

  public logout() {
    this.authService.logout();
    this.router.navigate(["/login"]);
  }

  // @HostListener('window:beforeunload', ['$event'])
  // unloadHandler(event: Event) {
  //   this.logout();
  // }

  isFullScreen = false;

  toggleFullScreen(): void {
    if (!this.isFullScreen) {
      // Entrer en plein écran
      const elem = document.documentElement; // la page entière
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if ((elem as any).webkitRequestFullscreen) {
        /* Safari */
        (elem as any).webkitRequestFullscreen();
      } else if ((elem as any).msRequestFullscreen) {
        /* IE11 */
        (elem as any).msRequestFullscreen();
      }
      this.isFullScreen = true;
      this.menuService.toggleSidebar();
    } else {
      // Quitter le plein écran
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        /* Safari */
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        /* IE11 */
        (document as any).msExitFullscreen();
      }
      this.isFullScreen = false;
      this.menuService.toggleSidebar();
    }
  }
}
